import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Sparkles, X } from 'lucide-react';
import { apiClient } from '../../api';
import { useChat } from '../../contexts/ChatContext';
import { monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, errMessage } from '../UI/ink';
import { parseTs } from '../SecurityAlerts/model';

export const IMP_WORD = (n) => (n >= 10 ? 'Mission' : n >= 9 ? 'Critical' : n >= 7 ? 'High' : n >= 4 ? 'Medium' : n >= 2 ? 'Low' : 'Minimal');
export const LEVEL = (r) => (r >= 75 ? 'crit' : r >= 50 ? 'high' : r >= 25 ? 'med' : 'low');
const LEVEL_WORD = { crit: 'CRITICAL', high: 'HIGH', med: 'MEDIUM', low: 'LOW' };
export const SEEN = {
  online: { dot: 'var(--sev-good-fg)', border: 'transparent', word: 'Reporting' },
  stale: { dot: 'var(--sev-med-fg)', border: 'transparent', word: 'Stale' },
  offline: { dot: 'transparent', border: 'var(--ink-faint)', word: 'Not reporting' },
  never: { dot: 'transparent', border: 'var(--ink-faint)', word: 'Never checked in' },
  archived: { dot: 'transparent', border: 'var(--surf-line-strong)', word: 'Archived' },
};
export const isNetworkDevice = (a) => /network|router|switch|firewall|gateway/i.test(a.device_type || '') || /network|snmp/i.test(a.os_info || '');

export const agoLong = (iso) => {
  const d = parseTs(iso);
  if (!d) return null;
  const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)} h ago`;
  return `${Math.floor(m / 1440)} d ago`;
};
const stamp = (iso) => {
  const d = parseTs(iso);
  return d ? d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : null;
};
const dateOnly = (iso) => { const d = parseTs(iso); return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null; };

const Label = ({ children, right }) => (
  <span className="flex items-baseline justify-between gap-3"><span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>{children}</span>{right}</span>
);

const SEG = [
  ['importance', 'Importance', 'var(--ink-muted)'],
  ['vulnerabilities', 'Vulnerabilities', 'var(--sev-med-fg)'],
  ['threats', 'Threats & findings', 'var(--sev-high-fg)'],
  ['confidence_bonus', 'Confidence', 'var(--surf-line-strong)'],
];

const riskWhy = (a) => {
  const b = a.breakdown || {};
  if (a.state === 'offline' || a.state === 'never') return a.state === 'never' ? 'Only its importance: the agent has never sent data, so nothing has been checked.' : 'Low partly because nothing new has been checked while the agent is silent.';
  const parts = [];
  if (b.threats > 0) parts.push(`${a.f ? `${a.f} open finding${a.f === 1 ? '' : 's'}` : ''}${a.f && a.ioc ? ' and ' : ''}${a.ioc ? `${a.ioc} IoC alert${a.ioc === 1 ? '' : 's'}` : ''}`);
  if (b.vulnerabilities > 0) parts.push(`${a.cve} open CVE${a.cve === 1 ? '' : 's'}`);
  if (!parts.length) return isNetworkDevice(a) ? 'Its importance only. With no agent, there are no packages to check for CVEs.' : 'Mostly its importance. Nothing active is open on it.';
  const biggest = [...SEG].sort((x, y) => (b[y[0]] || 0) - (b[x[0]] || 0))[0][1].toLowerCase();
  return `Mostly from ${biggest}: ${parts.join(', plus ')}. Recent and corroborated activity adds the confidence bonus.`;
};

const ItemRow = ({ to, kind, sev, title, meta }) => {
  const t = { critical: 'crit', high: 'high', medium: 'med', low: 'low', crit: 'crit', med: 'med' }[String(sev || '').toLowerCase()];
  return (
    <Link to={to} className="flex items-center gap-2.5 px-2.5 py-2 rounded-[9px] border min-w-0 hover:border-[color:var(--surf-line-strong)]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
      <span className="inline-flex items-center gap-[5px] h-5 shrink-0 px-1.5 rounded-[5px] font-mono text-[9.5px] font-medium tracking-[0.06em]" style={{ background: t ? `var(--sev-${t}-bg)` : 'var(--surf-raised)' }}>
        <span className="w-[5px] h-[5px] rounded-[1px] box-border" style={{ background: t ? `var(--sev-${t}-fg)` : 'transparent', border: t ? 'none' : '1px solid var(--ink-faint)' }} />{kind}
      </span>
      <span className="min-w-0 flex flex-col gap-0.5">
        <span className="text-[12.5px] font-medium truncate">{title}</span>
        <span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{meta}</span>
      </span>
    </Link>
  );
};

export default function AssetPane({ asset: a, baseline, onClose, onUpdate, onLifecycle }) {
  const { openChatWithServer } = useChat();
  const [detail, setDetail] = useState(null);
  const [menu, setMenu] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'archive' | 'discover' | 'restore'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loc, setLoc] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    setMenu(false); setConfirm(null); setError(null); setLoc(a?.location || '');
    if (!a) return;
    setDetail(null);
    const id = a.id;
    Promise.allSettled([
      apiClient.get(`/api/servers/${id}/snapshot`),
      apiClient.get(`/api/findings?server_id=${encodeURIComponent(id)}&status=new,triaged,investigating,contained&limit=20`),
      apiClient.get(`/api/alerts/ioc?server_id=${encodeURIComponent(id)}&resolved=0`),
      apiClient.get(`/api/alerts/cve?server_id=${encodeURIComponent(id)}&resolved=0`),
    ]).then(([snap, f, ioc, cve]) => {
      setDetail({
        snapshot: snap.status === 'fulfilled' ? snap.value.data?.snapshot : null,
        packages: snap.status === 'fulfilled' ? (snap.value.data?.packages || []).length : null,
        findings: f.status === 'fulfilled' ? f.value.data?.findings || [] : [],
        iocs: ioc.status === 'fulfilled' ? ioc.value.data?.ioc_alerts || [] : [],
        cves: cve.status === 'fulfilled' ? cve.value.data?.cve_alerts || [] : [],
      });
    });
  }, [a?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  if (!a) {
    return (
      <aside aria-label="Asset details" className="min-h-[320px] rounded-[14px] border flex flex-col items-center justify-center gap-2.5 p-8 text-center" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
        <span className="text-[15px] font-semibold">Select an asset</span>
        <span className="text-[13px] max-w-[300px]" style={{ color: 'var(--ink-muted)' }}>Why its risk is what it is, what’s open on it, and how it’s monitored show here.</span>
      </aside>
    );
  }

  const archived = a.lifecycle === 'ARCHIVED';
  const net = isNetworkDevice(a);
  const sn = SEEN[archived ? 'archived' : a.state];
  const lv = LEVEL(a.risk);
  const b = a.breakdown || {};
  const total = SEG.reduce((t, [k]) => t + (b[k] || 0), 0) || 1;
  const open = (detail?.findings.length || 0) + (detail?.iocs.length || 0) + (detail?.cves.length || 0);
  const snap = detail?.snapshot;
  const ports = Array.isArray(snap?.ports) ? snap.ports.map(p => (typeof p === 'object' ? p.port ?? p.local_port ?? JSON.stringify(p) : p)) : [];

  const notice = archived
    ? { title: 'Archived', text: 'It no longer shows in My Assets or counts towards the organisation’s risk. Its alerts and history are kept.', btn: 'Restore', act: () => setConfirm('restore') }
    : a.state === 'offline' || a.state === 'never'
      ? {
        title: a.state === 'never' ? 'The agent has never checked in' : `Not reporting for ${agoLong(a.last_seen)?.replace(' ago', '')}`,
        text: a.state === 'never' ? 'It was registered but no heartbeat has arrived, so nothing on it has been checked.' : `The agent last sent a heartbeat ${stamp(a.last_seen)}. Its risk score is stale: nothing new is being checked.`,
        link: ['/agents', 'Agent install and troubleshooting'], btn: 'Archive it instead', act: () => setConfirm('archive'), tone: 'var(--sev-low-bg)',
      }
      : net ? { title: 'Monitored by SNMP only', text: 'Routers and switches can’t run the agent, so there are no packages, CVE matches or baseline. Findings come from traffic and discovery.', link: [`/assets/${a.id}`, 'SNMP readings on the asset page'] } : null;

  const baselineText = net ? 'Not available without an agent'
    : !baseline || baseline.status === 'none' ? 'Not set'
      : baseline.status === 'expired' || (baseline.baseline_expires_at && parseTs(baseline.baseline_expires_at) < new Date()) ? `Expired ${dateOnly(baseline.baseline_expires_at)} · set a new one`
        : baseline.status === 'waiting' ? `Set ${dateOnly(baseline.baseline_set_at)} · waiting for a snapshot`
          : baseline.status === 'drift' ? `Set ${dateOnly(baseline.baseline_set_at)} · ${baseline.drift?.total} change${baseline.drift?.total === 1 ? '' : 's'} since`
            : `Set ${dateOnly(baseline.baseline_set_at)} · no changes`;

  const mon = archived ? [['Location', a.location || '—'], ['Importance', String(a.importance)]]
    : net ? [['Monitoring', `SNMP · last seen ${agoLong(a.last_seen) || 'never'}`], ['Agent', 'None · can’t run one'], ['Baseline', baselineText]]
      : [
        ['Agent', snap?.agent_version ? `${snap.agent_version} · heartbeat ${agoLong(a.last_seen) || 'never'}` : `heartbeat ${agoLong(a.last_seen) || 'never'}`],
        ['Last snapshot', snap ? `${agoLong(snap.captured_at)}${detail?.packages != null ? ` · ${detail.packages} packages` : ''}` : detail ? 'None yet' : '…'],
        ['Baseline', baselineText],
        ['Open ports', ports.length ? ports.slice(0, 12).join(', ') + (ports.length > 12 ? ` +${ports.length - 12}` : '') : snap ? 'None reported' : '…'],
      ];

  const items = [
    ...(detail?.findings || []).map(f => ({ key: f.id, to: `/findings?finding=${f.id}`, kind: 'FINDING', sev: f.severity, title: f.title, meta: `${f.id} · ${f.status}` })),
    ...(detail?.iocs || []).map(x => ({ key: `ioc-${x.id}`, to: `/security-alerts?alert=ioc-${x.id}`, kind: 'IOC', sev: x.threat_level, title: `${x.ioc_type} ${x.ioc_value}`, meta: `IoC alert ${x.id} · ${agoLong(x.detected_at)}` })),
    ...(detail?.cves || []).map(x => ({ key: `cve-${x.id}`, to: `/security-alerts?alert=cve-${x.id}`, kind: 'CVE', sev: x.severity, title: `${x.cve_id} · ${x.package_name}`, meta: `${x.cvss_score != null ? `CVSS ${x.cvss_score} · ` : ''}${agoLong(x.detected_at)}` })),
  ];

  const save = async (patch) => {
    setError(null);
    const ok = await onUpdate(a.id, patch);
    if (!ok) setError('Could not save that change.');
  };

  const doConfirm = async () => {
    setBusy(true); setError(null);
    try {
      await onLifecycle(a.id, confirm === 'archive' ? 'ARCHIVED' : confirm === 'discover' ? 'DISCOVERED' : 'REGISTERED');
      setConfirm(null);
    } catch (err) {
      setError(errMessage(err, 'That didn’t work.'));
    } finally {
      setBusy(false);
    }
  };
  const C = {
    archive: [`Archive ${a.name}?`, 'It moves to Archived and stops counting towards risk. Its alerts and history stay, and you can restore it.', 'Archive'],
    discover: [`Move ${a.name} back to Discovered?`, 'It leaves My Assets and shows on the Network Map as Discovered. Its alerts and history stay.', 'Move to Discovered'],
    restore: [`Restore ${a.name}?`, 'It returns to My Assets and counts towards risk again.', 'Restore'],
  }[confirm] || [];

  return (
    <aside aria-label="Asset details" className="relative rounded-[14px] border flex flex-col" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      <div className="flex flex-col gap-[9px] px-[22px] pt-[18px] pb-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10.5px] tracking-[0.12em] truncate" style={{ color: 'var(--ink-link)' }}>ASSET · {a.kindLabel}</span>
          <button type="button" onClick={onClose} aria-label="Close details" className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}><X className="w-[13px] h-[13px]" strokeWidth={2} /></button>
        </div>
        <h2 className="m-0 font-heading text-[21px] font-semibold tracking-[-0.01em] break-words">{a.name}</h2>
        <span className="flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
          <span className="w-2 h-2 rounded-full box-border" style={{ background: sn.dot, border: `1px solid ${sn.border}` }} />
          {sn.word}{!archived && a.last_seen ? ` · ${agoLong(a.last_seen)}` : ''}
          <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{[a.ip, a.os].filter(Boolean).join(' · ')}</span>
        </span>
      </div>

      <div className="flex flex-col gap-4 px-[22px] py-4">
        {notice && (
          <div className="flex flex-col gap-2 px-3.5 py-3 rounded-[10px]" style={{ background: notice.tone || 'var(--surf-raised)' }}>
            <span className="text-[13.5px] font-semibold">{notice.title}</span>
            <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{notice.text}</span>
            {(notice.link || notice.btn) && (
              <span className="flex flex-wrap gap-3.5">
                {notice.link && <Link to={notice.link[0]} className="text-[12.5px] hover:underline" style={{ color: 'var(--ink-link)' }}>{notice.link[1]}</Link>}
                {notice.btn && <button type="button" onClick={notice.act} className="text-[12.5px] underline" style={{ color: 'var(--ink-link)' }}>{notice.btn}</button>}
              </span>
            )}
          </div>
        )}

        {!archived && (
          <div className="flex flex-col gap-[9px]">
            <Label>RISK · WHY IT’S {Math.round(a.risk)}</Label>
            <div className="flex items-baseline gap-2.5">
              <span className="font-heading text-[34px] font-semibold leading-none">{Math.round(a.risk)}</span>
              <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>/ 100</span>
              <span className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md font-mono text-[10.5px] font-medium tracking-[0.06em]" style={{ background: `var(--sev-${lv}-bg)` }}>
                <span className="w-1.5 h-1.5 rounded-[1px]" style={{ background: `var(--sev-${lv}-fg)` }} />{LEVEL_WORD[lv]}
              </span>
            </div>
            {a.breakdown && (
              <>
                <div className="flex h-2 rounded overflow-hidden gap-0.5" style={{ background: 'var(--surf-line)' }} aria-hidden="true">
                  {SEG.map(([k, , c]) => (b[k] > 0 ? <span key={k} style={{ width: `${Math.min(100, (b[k] / Math.max(total, 100)) * 100)}%`, background: c }} /> : null))}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {SEG.map(([k, label, c]) => (
                    <span key={k} className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
                      <span className="w-2 h-2 shrink-0 rounded-sm" style={{ background: c }} />
                      <span className="flex-grow">{label}</span>
                      <span className="font-mono text-xs" style={{ color: 'var(--ink-text)' }}>{k === 'confidence_bonus' ? '+' : ''}{Math.round(b[k] || 0)}</span>
                    </span>
                  ))}
                </div>
              </>
            )}
            <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-faint)' }}>{riskWhy(a)}</span>
          </div>
        )}

        {!archived && (
          <div className="flex flex-col gap-1.5">
            <Label right={items.length ? <Link to={`/assets/${a.id}`} className="text-xs hover:underline" style={{ color: 'var(--ink-link)' }}>See all</Link> : null}>OPEN ON THIS ASSET · {detail ? open : '…'}</Label>
            {detail && !items.length && <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Nothing open.</span>}
            {items.slice(0, 5).map(it => <ItemRow key={it.key} {...it} />)}
            {items.length > 5 && <Link to={`/assets/${a.id}`} className="text-[12.5px] hover:underline" style={{ color: 'var(--ink-link)' }}>{items.length - 5} more on the asset page</Link>}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Label>PROFILE</Label>
          <div className="grid grid-cols-2 gap-2.5">
            <label className="flex flex-col gap-[5px] text-xs" style={{ color: 'var(--ink-faint)' }}>Importance
              <select value={String(a.importance)} onChange={e => save({ criticality_score: Number(e.target.value) })} className="h-[38px] box-border px-2 rounded-[9px] border text-[13px]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>
                {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} · {IMP_WORD(n)}{n === 5 ? ' (default)' : ''}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-[5px] text-xs" style={{ color: 'var(--ink-faint)' }}>Location
              <input
                type="text"
                value={loc}
                onChange={e => setLoc(e.target.value)}
                onBlur={() => { if (loc.trim() !== (a.location || '')) save({ location: loc.trim() || null }); }}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                placeholder="e.g. Data centre · Rack B2"
                className="h-[38px] box-border px-2.5 rounded-[9px] border text-[13px] outline-none focus:border-[color:var(--accent)]"
                style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
              />
            </label>
          </div>
          <label className="flex flex-wrap items-center gap-2.5 min-h-9 text-[13px] cursor-pointer">
            <input type="checkbox" checked={!!a.inet} onChange={e => save({ is_internet_facing: e.target.checked ? 1 : 0 })} className="m-0 w-4 h-4" style={{ accentColor: 'var(--accent)' }} />
            Internet-facing<span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Shown as PUBLIC. Not part of the risk score yet.</span>
          </label>
          <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>Importance feeds the risk score (up to 20 points). It doesn’t change on its own.</span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>MONITORING</Label>
          {mon.map(([k, v]) => (
            <span key={k} className="grid grid-cols-[110px_minmax(0,1fr)] gap-2.5 text-[12.5px] leading-normal">
              <span style={{ color: 'var(--ink-faint)' }}>{k}</span><span className="break-words" style={{ color: 'var(--ink-muted)' }}>{v}</span>
            </span>
          ))}
        </div>
        {error && <span role="alert" className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
      </div>

      <div className="relative mt-auto flex flex-wrap items-center gap-2 px-[22px] py-3.5 border-t" style={{ borderColor: 'var(--surf-line)' }}>
        {archived ? (
          <button type="button" onClick={() => setConfirm('restore')} className={primaryBtn} style={primaryBtnStyle}>Restore to My Assets</button>
        ) : (
          <>
            <Link to={`/assets/${a.id}`} className={primaryBtn} style={primaryBtnStyle}>Open asset page</Link>
            <button type="button" onClick={() => openChatWithServer(a.id, a.name)} className={secondaryBtn} style={secondaryBtnStyle}>
              <Sparkles className="w-[15px] h-[15px]" strokeWidth={1.9} style={{ color: 'var(--accent)' }} />Ask about it
            </button>
            <span ref={menuRef} className="ml-auto relative">
              <button type="button" onClick={() => { setMenu(v => !v); setConfirm(null); }} aria-label="More actions" aria-haspopup="menu" aria-expanded={menu} className="w-11 h-11 flex items-center justify-center rounded-[10px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {menu && (
                <div role="menu" className="absolute right-0 bottom-[52px] z-20 w-[250px] flex flex-col p-1.5 rounded-[10px] border" style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}>
                  {[['archive', 'Archive', 'Hide it; keep its history'], ['discover', 'Move back to Discovered', 'Return it to the Network Map']].map(([k, t, d]) => (
                    <button key={k} type="button" role="menuitem" onClick={() => { setMenu(false); setConfirm(k); }} className="min-h-11 flex flex-col justify-center gap-0.5 px-2.5 py-1.5 rounded-[7px] text-left hover:bg-[color:var(--surf-surface)]">
                      <span className="text-[13px] font-medium">{t}</span><span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{d}</span>
                    </button>
                  ))}
                </div>
              )}
            </span>
          </>
        )}
        {confirm && (
          <div role="dialog" aria-label="Confirm" className="absolute left-3.5 right-3.5 bottom-[66px] z-20 flex flex-col gap-2.5 p-3.5 rounded-xl border" style={{ background: 'var(--surf-raised)', borderColor: 'var(--accent)', boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}>
            <span className="text-sm font-semibold">{C[0]}</span>
            <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{C[1]}</span>
            <span className="flex gap-2">
              <button type="button" disabled={busy} onClick={doConfirm} className="h-[38px] px-3.5 rounded-[9px] text-[13.5px] font-semibold disabled:opacity-50" style={primaryBtnStyle}>{busy ? 'Working…' : C[2]}</button>
              <button type="button" onClick={() => setConfirm(null)} className="h-[38px] px-3 text-[13.5px]" style={{ color: 'var(--ink-muted)' }}>Cancel</button>
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}
